import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as AcceptIcon,
  Cancel as DeclineIcon,
  Send as SendIcon,
  Group as TeamIcon,
  Schedule as ScheduleIcon,
  EmojiEvents as TrophyIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { TournamentInvitation } from "../types";
import { apiRequest } from "../config/api";
import { useAuth } from "../contexts/AuthContext";
import { format, formatDistanceToNow } from "date-fns";
import SafeAvatar from "./SafeAvatar";

interface InvitationManagerProps {
  onInvitationUpdate?: () => void;
  onPendingCountChange?: (count: number) => void;
  hideEmptyState?: boolean;
  historyOnly?: boolean;
}

export default function InvitationManager({
  onInvitationUpdate,
  onPendingCountChange,
  hideEmptyState,
  historyOnly,
}: InvitationManagerProps) {
  const { playerToken, playerUser } = useAuth();
  const [receivedInvitations, setReceivedInvitations] = useState<
    TournamentInvitation[]
  >([]);
  const [sentInvitations, setSentInvitations] = useState<
    TournamentInvitation[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] =
    useState<TournamentInvitation | null>(null);
  const [declineReason, setDeclineReason] = useState("");

  // Helper function to convert Firebase timestamp to Date
  const toDate = (timestamp: any): Date => {
    if (!timestamp) return new Date();
    if (timestamp instanceof Date) return timestamp;
    if (timestamp._seconds) {
      return new Date(timestamp._seconds * 1000);
    }
    if (timestamp.toDate) {
      return timestamp.toDate();
    }
    return new Date(timestamp);
  };

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    setLoading(true);
    setError("");

    try {
      const [receivedRes, sentRes] = await Promise.all([
        apiRequest("/api/tournament-invitations/received", {
          headers: { Authorization: `Bearer ${playerToken}` },
        }),
        apiRequest("/api/tournament-invitations/sent", {
          headers: { Authorization: `Bearer ${playerToken}` },
        }),
      ]);

      const received = await receivedRes.json();
      const sent = await sentRes.json();

      setReceivedInvitations(Array.isArray(received) ? received : []);
      setSentInvitations(Array.isArray(sent) ? sent : []);
    } catch (err: any) {
      setError(err.message || "Failed to load invitations");
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async (invitationId: string) => {
    setProcessingId(invitationId);
    setError("");

    try {
      await apiRequest(`/api/tournament-invitations/${invitationId}/accept`, {
        method: "POST",
        headers: { Authorization: `Bearer ${playerToken}` },
      });

      await fetchInvitations();
      if (onInvitationUpdate) {
        onInvitationUpdate();
      }
    } catch (err: any) {
      setError(err.message || "Failed to accept invitation");
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeclineInvitation = async () => {
    if (!selectedInvitation) return;

    setProcessingId(selectedInvitation.id);
    setError("");

    try {
      await apiRequest(
        `/api/tournament-invitations/${selectedInvitation.id}/decline`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${playerToken}` },
          body: JSON.stringify({
            reason: declineReason || undefined,
          }),
        },
      );

      await fetchInvitations();
      if (onInvitationUpdate) {
        onInvitationUpdate();
      }
      setDeclineDialogOpen(false);
      setDeclineReason("");
      setSelectedInvitation(null);
    } catch (err: any) {
      setError(err.message || "Failed to decline invitation");
    } finally {
      setProcessingId(null);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    setProcessingId(invitationId);
    setError("");

    try {
      await apiRequest(`/api/tournament-invitations/${invitationId}/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${playerToken}` },
      });

      await fetchInvitations();
      if (onInvitationUpdate) {
        onInvitationUpdate();
      }
    } catch (err: any) {
      setError(err.message || "Failed to cancel invitation");
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusChip = (status: TournamentInvitation["status"]) => {
    switch (status) {
      case "pending":
        return <Chip label="Pending" color="warning" size="small" />;
      case "accepted":
        return <Chip label="Accepted" color="success" size="small" />;
      case "accepted-auto":
        return (
          <Chip
            label="Auto-Confirmed 🎉"
            color="success"
            size="small"
            variant="filled"
          />
        );
      case "declined":
        return <Chip label="Declined" color="error" size="small" />;
      case "expired":
        return <Chip label="Expired" color="default" size="small" />;
      case "canceled":
        return <Chip label="Canceled" color="default" size="small" />;
      default:
        return null;
    }
  };

  const isExpiringSoon = (expiresAt: Date) => {
    const hoursRemaining =
      (new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60);
    return hoursRemaining > 0 && hoursRemaining < 24;
  };

  const pendingReceived = receivedInvitations.filter(
    (inv) => inv.status === "pending",
  );
  const completedReceived = receivedInvitations.filter(
    (inv) => inv.status !== "pending",
  );
  const pendingSent = sentInvitations.filter((inv) => inv.status === "pending");
  const completedSent = sentInvitations.filter(
    (inv) => inv.status !== "pending",
  );

  useEffect(() => {
    if (!loading) onPendingCountChange?.(pendingReceived.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, pendingReceived.length]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Pending Received Invitations */}
      {!historyOnly && pendingReceived.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <ScheduleIcon /> Pending Invitations ({pendingReceived.length})
          </Typography>
          {pendingReceived.map((invitation) => (
            <Card
              key={invitation.id}
              sx={{
                mb: 2,
                border: "2px solid",
                borderColor: isExpiringSoon(invitation.expiresAt)
                  ? "warning.main"
                  : "primary.main",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      🎾 {invitation.tournamentName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>From:</strong> {invitation.senderPlayerName}
                      {invitation.senderLevel &&
                        ` (Level ${invitation.senderLevel})`}
                    </Typography>
                    {invitation.teamName && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Team:</strong> {invitation.teamName}
                      </Typography>
                    )}
                  </Box>
                  {getStatusChip(invitation.status)}
                </Box>

                {invitation.personalMessage && (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" fontStyle="italic">
                      "{invitation.personalMessage}"
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <Typography variant="caption" color="text.secondary">
                    Sent {formatDistanceToNow(toDate(invitation.createdAt))} ago
                  </Typography>
                  {isExpiringSoon(invitation.expiresAt) && (
                    <Chip
                      icon={<WarningIcon />}
                      label={`Expires in ${Math.floor((toDate(invitation.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60))} hours`}
                      size="small"
                      color="warning"
                    />
                  )}
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: "flex-end" }}>
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedInvitation(invitation);
                    setDeclineDialogOpen(true);
                  }}
                  disabled={processingId !== null}
                  startIcon={<DeclineIcon />}
                >
                  Decline
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleAcceptInvitation(invitation.id)}
                  disabled={processingId !== null}
                  startIcon={
                    processingId === invitation.id ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <AcceptIcon />
                    )
                  }
                >
                  {processingId === invitation.id
                    ? "Accepting..."
                    : "Accept Team"}
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Sent Invitations */}
      {!historyOnly && pendingSent.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <SendIcon /> Sent Invitations ({pendingSent.length})
          </Typography>
          {pendingSent.map((invitation) => (
            <Card key={invitation.id} sx={{ mb: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 1,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1">
                      🎾 {invitation.tournamentName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>To:</strong>{" "}
                      {invitation.recipientName || invitation.recipientEmail}
                    </Typography>
                    {invitation.teamName && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Team:</strong> {invitation.teamName}
                      </Typography>
                    )}
                  </Box>
                  {getStatusChip(invitation.status)}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Sent {formatDistanceToNow(toDate(invitation.createdAt))} ago
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: "flex-end" }}>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleCancelInvitation(invitation.id)}
                  disabled={processingId !== null}
                >
                  Cancel Invitation
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* History */}
      {(completedReceived.length > 0 || completedSent.length > 0) && (
        <Accordion defaultExpanded={historyOnly}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              Invitation History (
              {completedReceived.length + completedSent.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {completedReceived.map((invitation) => (
              <Box
                key={invitation.id}
                sx={{
                  mb: 2,
                  pb: 2,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="body2">
                      <strong>{invitation.tournamentName}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      From {invitation.senderPlayerName}
                    </Typography>
                  </Box>
                  {getStatusChip(invitation.status)}
                </Box>
              </Box>
            ))}
            {completedSent.map((invitation) => (
              <Box
                key={invitation.id}
                sx={{
                  mb: 2,
                  pb: 2,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="body2">
                      <strong>{invitation.tournamentName}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      To {invitation.recipientName || invitation.recipientEmail}
                    </Typography>
                  </Box>
                  {getStatusChip(invitation.status)}
                </Box>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Empty State */}
      {!hideEmptyState && receivedInvitations.length === 0 && sentInvitations.length === 0 && (
        <Alert severity="info">
          No invitations yet. Browse available tournaments to get started!
        </Alert>
      )}

      {/* Decline Dialog */}
      <Dialog
        open={declineDialogOpen}
        onClose={() => setDeclineDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Decline Invitation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Are you sure you want to decline this invitation?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (Optional)"
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="Let them know why you can't make it..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeclineInvitation}
            color="error"
            variant="contained"
            disabled={processingId !== null}
          >
            Decline
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
